import { UploadImageField } from "@/common/components/form/UploadImageField/UploadImageField";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { addPhotoVisualRecord } from "@/common/services/vehicles.api";
import { UploadDocumentFieldFormik } from "@/modules/renters/components/forms/fields/UploadDocumentFieldFormik";
import { IVehicleVisualRecord } from "@/types/vehicle-visual-record.type";
import { Modal } from "flowbite-react";
import { FormikProvider, useFormik, FieldArray } from "formik";
import AutocompleteFormik from "@/common/components/form/AutocompleteFormik";
import { useEffect, useMemo } from "react";

type Props = {
  closeAndUpdate: (data: { eventName: string, data: IVehicleVisualRecord }) => void;
  visualRecordId: string;
  vehicleId: string;
  onClose: () => void;
  docType: string
}

interface PhotoTypeOption {
  label: string;
}

interface PhotoFormValues {
  fileName: string;
  photoType: string;
  photoTypeCombobox: PhotoTypeOption | null;
}

interface FormValues {
  visualRecordId: string;
  photos: PhotoFormValues[];
}

function ModalAddPhotos(props: Props) {

  const pathToUpload = `vehicles/${props.vehicleId}/${props.docType}`


  const photoTypeOptions = useMemo(() => [
    { label: "Frente do veículo" },
    { label: "Lateral direita do veículo" },
    { label: "Lateral esquerda do veículo" },
    { label: "Traseira do veículo" },
    { label: "Odômetro" },
    { label: "Foto com o cliente" },
  ], []);

  const formik = useFormik<FormValues>({
    initialValues: {
      visualRecordId: props.visualRecordId,
      photos: [
        {
          fileName: '',
          photoType: '',
          photoTypeCombobox: null as PhotoTypeOption | null
        }
      ]
    },
    onSubmit: async (data) => {

      const validPhotos = data.photos.filter(photo => photo.fileName);

      if (validPhotos.length === 0) {
        console.warn('Nenhuma foto foi selecionada para upload');
        return;
      }

      // Montar um objeto com todas as fotos
      const photosToSend = validPhotos.map(photo => ({
        content: photo.fileName,
        type: photo.photoTypeCombobox?.label || photo.photoType || 'Sem especificação'
      }));

      const dataToSend = {
        fileName: props.docType,
        visualRecordId: props.visualRecordId,
        photos: photosToSend // Array com todas as fotos
      };

      try {
        const result = await addPhotoVisualRecord(dataToSend);

        if (result.ok && result.data) {
          props.closeAndUpdate({
            eventName: 'photosAdded',
            data: result.data
          });
        } else {
          throw new Error('Upload falhou');
        }
      } catch (error) {
        console.error('💥 Erro durante o upload:', error);
      }
    }
  });


  useEffect(() => {
    formik.values.photos.forEach((photo, index) => {
      if (photo.photoTypeCombobox?.label && photo.photoTypeCombobox.label !== photo.photoType) {
        formik.setFieldValue(`photos.${index}.photoType`, photo.photoTypeCombobox.label);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.photos]);

  return (
    <Modal className="relative" show={true} onClose={props.onClose} size="5xl">
      <Modal.Header className="text-black">
        <h2 className="dark:text-black">Adicionar Fotos</h2>
      </Modal.Header>
      <Modal.Body>
        <FormikProvider value={formik}>
          <FieldArray name="photos">
            {({ push, remove }) => (
              <div className="space-y-6">
                {formik.values.photos.map((_photo, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      {formik.values.photos.length > 1 && (
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          ✕ Remover
                        </button>
                      )}
                    </div>

                    {/* Layout lado a lado */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Coluna esquerda - Descrição */}
                      <div className="space-y-4">
                        <AutocompleteFormik
                          label="Tipo da foto"
                          name={`photos.${index}.photoTypeCombobox`}
                          valueProperty="label"
                          options={photoTypeOptions}
                          allowNewItem={true}
                          className="w-full"
                        />
                      </div>

                      {/* Coluna direita - Upload */}
                      <div className="space-y-4">
                        <UploadDocumentFieldFormik
                          name={`photos.${index}.fileName`}
                          pathToUpload={pathToUpload}
                          uploadName={
                            formik.values.photos[index]?.photoTypeCombobox?.label ||
                            formik.values.photos[index]?.photoType ||
                            `foto-${index + 1}`
                          }
                        >
                          {({ src, onNewFile, onRemoveClick }) => (
                            <UploadImageField
                              src={src}
                              onNewFile={onNewFile}
                              onRemoveClick={onRemoveClick}
                              className="w-full"
                            />
                          )}
                        </UploadDocumentFieldFormik>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Botão para adicionar mais documentos */}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => push({
                      fileName: '',
                      photoType: '',
                      photoTypeCombobox: null
                    })}
                    className="flex items-center space-x-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-2xl text-gray-400">+</span>
                    <span className="text-sm text-gray-600">Adicionar mais fotos</span>
                  </button>
                </div>

                {/* Botões de ação */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={props.onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <ButtonWithLoader
                    // isLoading={formik.isSubmitting}
                    onClick={formik.handleSubmit}
                    disabled={!formik.values.photos.some(photo => photo.fileName)}
                  >
                    Salvar Fotos
                  </ButtonWithLoader>
                </div>
              </div>
            )}
          </FieldArray>
        </FormikProvider>
      </Modal.Body>
    </Modal>
  )
}

export default ModalAddPhotos;
